/**
 * "Which list?" (#337): type to filter, arrows and Enter to pick, Escape to
 * close. The last row makes a new list from the typed text.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListTodo, Plus } from 'lucide-react';
import { DismissBackdrop } from '../../DismissBackdrop';

export interface ListPickerProps {
  x: number;
  y: number;
  lists: ReadonlyArray<{ listId: string; name: string }>;
  filter?: string;
  onPick: (listId: string) => void;
  onCreate: (name: string) => void;
  onClose: () => void;
}

const MENU_WIDTH = 260;

export function ListPicker({ x, y, lists, filter = '', onPick, onCreate, onClose }: ListPickerProps) {
  const [query, setQuery] = useState(filter);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => lists.filter((l) => l.name.toLowerCase().includes(q)), [lists, q]);
  const canCreate = query.trim().length > 0 && !lists.some((l) => l.name.toLowerCase() === q);
  const rows = shown.length + (canCreate ? 1 : 0);

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [onClose]);

  const choose = (i: number) => {
    if (i < shown.length) onPick(shown[i].listId);
    else if (canCreate) onCreate(query.trim());
  };
  const left = Math.max(8, Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : 1024) - MENU_WIDTH - 8));
  const row = 'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm';

  return createPortal(
    <>
    {/* #121: a tap outside only closes the picker - it does not also land on what is underneath. */}
    <DismissBackdrop zClass="z-[59]" onDismiss={onClose} />
    <div ref={ref} className="fixed z-[60] rounded-lg border border-border bg-bg-card p-1 shadow-lg" style={{ left, top: y + 4, width: MENU_WIDTH }}>
      <input
        autoFocus
        aria-label="Find a list"
        value={query}
        maxLength={200}
        placeholder="Add to list..."
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); onClose(); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(rows - 1, a + 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (rows) choose(active); }
        }}
        className="mb-1 w-full rounded-md border border-border-hover bg-bg-input px-2 py-1 text-sm text-text-primary"
      />
      <div role="listbox" aria-label="Lists" className="max-h-60 overflow-y-auto">
        {shown.map((l, i) => (
          <button key={l.listId} type="button" role="option" aria-selected={i === active} aria-label={l.name}
            className={`${row} ${i === active ? 'bg-bg-hover text-text-primary' : 'text-text-secondary'}`}
            onMouseEnter={() => setActive(i)} onClick={() => choose(i)}>
            <ListTodo size={14} aria-hidden /><span className="truncate">{l.name}</span>
          </button>
        ))}
        {canCreate && (
          <button type="button" role="option" aria-selected={active === shown.length} aria-label={`New list '${query.trim()}'`}
            className={`${row} ${active === shown.length ? 'bg-bg-hover text-text-primary' : 'text-text-secondary'}`}
            onMouseEnter={() => setActive(shown.length)} onClick={() => choose(shown.length)}>
            <Plus size={14} aria-hidden /><span className="truncate">New list '{query.trim()}'</span>
          </button>
        )}
        {!rows && <div className="px-2 py-1.5 text-sm text-text-muted">No lists yet - type a name to make one</div>}
      </div>
    </div>
    </>,
    document.body,
  );
}
